import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, UserRound } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Select, TimePicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { HR_TAB_PATH, useEmployeeOptions } from './employee-options';

const isoDate = (value: string | null) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : todayIso());

export function MarkAttendancePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), HR_TAB_PATH.attendance);
  const [date, setDate] = useState(() => isoDate(params.get('date')));
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState('present');
  const [checkIn, setCheckIn] = useState('08:00');
  const [checkOut, setCheckOut] = useState('16:00');
  const [error, setError] = useState<string | null>(null);
  const employees = useEmployeeOptions(true);
  const timed = status === 'present' || status === 'half_day';

  const save = useMutation({
    mutationFn: () =>
      post('/hr/attendance', {
        employeeId,
        workDate: date,
        status,
        source: 'manual',
        checkInAt: timed ? new Date(`${date}T${checkIn}:00`).toISOString() : null,
        checkOutAt: timed ? new Date(`${date}T${checkOut}:00`).toISOString() : null,
      }),
    onSuccess: () => {
      toast.success('Attendance recorded');
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !save.isSuccess && (Boolean(employeeId) || status !== 'present' || checkIn !== '08:00' || checkOut !== '16:00');

  return (
    <FormScreen
      title="Record attendance"
      description="Lateness and overtime are measured against the rostered shift"
      icon={ClipboardCheck}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!employeeId) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'person')} icon={UserRound}>
        <Field label="Person" required>
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {employees.data?.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Date" required>
          <DatePicker value={date} onChange={setDate} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'attendance')} icon={ClipboardCheck}>
        <Field label="Status" required className="sm:col-span-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {['present', 'absent', 'leave', 'half_day', 'holiday', 'off'].map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </Select>
        </Field>
        {timed && (
          <>
            <Field label="Check in">
              <TimePicker value={checkIn} onChange={setCheckIn} />
            </Field>
            <Field label="Check out">
              <TimePicker value={checkOut} onChange={setCheckOut} />
            </Field>
          </>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Record" loading={save.isPending} incomplete={!employeeId} cancelTo={returnTo} />
    </FormScreen>
  );
}
